import { useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Container, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Helmet } from "react-helmet-async";
 

import { MainNavDrawer } from "./nav/MainNavDrawer";
import { MobileNavIcon } from "./nav/MobileNavIcon";
import { BackArrowButton } from "./BackArrowButton";
import { ActivityFeedButton } from "./activityFeed/ActivityFeedButton";
import { SettingsMenu } from "./settings/SettingsMenu";
import { Drawers } from "./Drawers";
import { GlobalNoticeComponent } from "./GlobalNoticeComponent";
import { SystemOutageBanner } from "./SystemOutageBanner";
import { CenturionMonthSelect } from "../screens/CenturionScreen/CenturionMonthSelect";

import { useUshopRedirectMarkets } from "../hooks/useUshopRedirectMarkets";
import { LanguageContext } from "../hooks/LanguageContext";
import { LANGUAGE_DETAILS } from "../common/languageDetails";
 
import { T } from "../hooks/useTranslate";
import { useTranslateKey } from "../hooks/useTranslate";
import { ThemeToggle } from "@unicity/design-system";
import { useMixPanel } from "../hooks/useMixPanel";
import { useAuth } from "../hooks/useAuth";

const OfficePage = ({
  children,
  pageTitle,
  showBackArrow,
  onBackArrowClick,
  isWebView = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { shopLink } = useUshopRedirectMarkets();
  const viewportIsSmall = useMediaQuery(theme.breakpoints.up("sm"));
  const viewportIsMedium = useMediaQuery(theme.breakpoints.up("md"));
  const viewportIsLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const { setCurrentLanguage } = useContext(LanguageContext);
  const { logEvent } = useMixPanel();
  const { userInfo } = useAuth();
  const onEvent = (data) => {
    logEvent("office_page_theme_toggle", {      
      unicity_id: userInfo?.unicityId,
      country_code: userInfo?.countryCode,
      route: "theme",
      app_version: process.env.REACT_APP_VERSION,
      ...data,
    });
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const langParam = params.get("lang");
    if (langParam) {
      // Find the corresponding i18nCode from LANGUAGE_DETAILS
      const languageDetail = LANGUAGE_DETAILS.find(lang => 
        lang.countryCode === langParam || lang.i18nCode === langParam
      );
      const i18nCode = languageDetail?.i18nCode || langParam;
      
      // Let the LanguageContext handle the change via design-system
      setCurrentLanguage(i18nCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const showBackArrowList = [
    "new_feel_great_members",
    "order_reminders",
    "potential_distributors",
    "partials",
    "unicity_news",
    "search_results",
    "statements"
  ];

  const handlePartnersClick = () => {
    if (pageTitle === "partners") {
      navigate("/partners");
    }
  };
  const effectiveTitleKey =
    pageTitle === "members" && userInfo?.isAffiliate ? "customers" : pageTitle;
  const translatedPageTitle = useTranslateKey(effectiveTitleKey);

  return (
    <Box sx={{ display: "flex" }}>
      {!isWebView && (viewportIsSmall || viewportIsMedium)
        && <MainNavDrawer
          shopLink={shopLink}
          viewportIsLarge={viewportIsLarge}
        />
      }
      <Container
        maxWidth={false}
        sx={{
          margin: viewportIsLarge && "0px 16px 0px 16px",
          padding: viewportIsLarge && "0px 0px",
        }}
      >
        <Helmet>{
          <>
            <title>{translatedPageTitle} | Unicity Office</title>
            <meta name="description" content="The ultimate tool for distributors to elevate their Unicity business. With comprehensive features for managing sales, tracking performance, and fostering growth." />
            <meta name="keywords" content="Unicity, Office, Distributor, Sales, Performance, Growth" />
            <meta property="og:title" content={`${translatedPageTitle} | Unicity Office`} />
            <meta property="og:description" content="The ultimate tool for distributors to elevate their Unicity business. With comprehensive features for managing sales, tracking performance, and fostering growth." />
            <meta property="og:image" content="https://cdn.builder.io/api/v1/image/assets%2F75d1550ccc71414e99b49b063f57488b%2F00ff8c196d45453ba2264c03594b4ceb" />
          </>
        }</Helmet>
        {!isWebView && (
          <Box
            className="flex-row"
            style={{
              padding: viewportIsSmall ? "19.5px 0px" : "11.25px 16px",
              gap: "12px"
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              {!viewportIsSmall && !showBackArrowList.includes(pageTitle) && (
                <MobileNavIcon shopLink={shopLink} />
              )}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "12.5px" }}
              >
                {showBackArrowList.includes(pageTitle) &&
                  showBackArrow && <BackArrowButton pageTitle={pageTitle} onBackArrowClick={onBackArrowClick} />}
                <Typography
                  data-test-id="page-title"
                  onClick={handlePartnersClick}
                  variant="h1"
                  sx={{
                    fontSize: {
                      "xs": "calc(17px * var(--unicity-font-scale, 1))", 
                      "sm": "calc(18px * var(--unicity-font-scale, 1))",
                      "md": "calc(20px * var(--unicity-font-scale, 1))"
                    },
                    fontWeight: "600",
                    cursor: pageTitle === "partners" && "pointer",
                  }}
                >
                  <T>{effectiveTitleKey}</T>
                </Typography>
                {viewportIsLarge && pageTitle === "dashboard" && <SystemOutageBanner />}
                {viewportIsLarge && pageTitle === "dashboard" && <GlobalNoticeComponent />}
              </Box>
              {pageTitle === "centurion" && viewportIsSmall && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CenturionMonthSelect isDashboard={false} />
                </Box>
              )}
            </Box>
            <Stack direction="row" spacing={1.5} flexShrink={0} sx={{ alignItems: "center" }}>
              <ActivityFeedButton />
              <ThemeToggle onEvent={onEvent} />
              <SettingsMenu />
            </Stack>
          </Box>
        )}
        <Box>
          {pageTitle === "centurion" && !viewportIsSmall && (
            <CenturionMonthSelect />
          )}
        </Box>
        <Box sx={{ marginBottom: viewportIsSmall && "30px" }}>{children}</Box>
        <Drawers />
      </Container>
    </Box>
  );
};

export { OfficePage };